import { roundWalkQty, writeOffRemaining } from "./packedAuditMath.js";

export const WASTE_WALK_CAUTION =
  "Counting this pile again records the waste again. Mark it and get it out of the waste area the first time you disposition it. If it is still sitting there on the next walk, leave it off.";

export type WasteLineSplit = {
  qtySeen: number;
  qtySalvage: number;
  qtyScrap: number;
};

export function splitWasteLine(
  seenRaw: number,
  salvageRaw: number,
): { ok: true; line: WasteLineSplit } | { ok: false; error: string } {
  if (!Number.isFinite(seenRaw) || seenRaw <= 0) {
    return { ok: false, error: "Quantity seen must be greater than zero." };
  }
  if (!Number.isFinite(salvageRaw) || salvageRaw < 0) {
    return { ok: false, error: "Quantity to use must be zero or more." };
  }
  const qtySeen = roundWalkQty(seenRaw);
  const qtySalvage = roundWalkQty(salvageRaw);
  if (qtySeen <= 0) return { ok: false, error: "Quantity seen must be greater than zero." };
  if (qtySalvage - qtySeen > 0.0005) {
    return { ok: false, error: "Quantity to use cannot exceed quantity seen." };
  }
  return {
    ok: true,
    line: {
      qtySeen,
      qtySalvage,
      qtyScrap: roundWalkQty(Math.max(0, qtySeen - qtySalvage)),
    },
  };
}

/** Scrap posts only what is still on the books. */
export function scrapPostQty(qtyScrap: number, onHand: number): number {
  const scrap = roundWalkQty(Math.max(0, qtyScrap));
  const available = roundWalkQty(Math.max(0, onHand));
  return roundWalkQty(Math.min(scrap, available));
}

export type WriteOffLot = {
  lineId: string;
  deltaQty: number;
  returnedQty: number;
  reversed: boolean;
};

export type SalvageAllocation = {
  lineId: string;
  qty: number;
};

/** Oldest unreverted write-off first. Salvage above that amount stays an instruction. */
export function allocateSalvageReturn(
  qtySalvage: number,
  lots: readonly WriteOffLot[],
): { postQty: number; allocations: SalvageAllocation[] } {
  let left = roundWalkQty(Math.max(0, qtySalvage));
  const allocations: SalvageAllocation[] = [];
  for (const lot of lots) {
    if (left <= 0.0005) break;
    const room = writeOffRemaining(lot.deltaQty, lot.returnedQty, lot.reversed);
    const take = roundWalkQty(Math.min(room, left));
    if (take <= 0.0005) continue;
    allocations.push({ lineId: lot.lineId, qty: take });
    left = roundWalkQty(left - take);
  }
  const postQty = roundWalkQty(allocations.reduce((sum, row) => sum + row.qty, 0));
  return { postQty, allocations };
}

export function wasteLineStillInPile(line: {
  qtyScrap: number;
  qtySalvage: number;
  removedFromArea: boolean;
  markedForUse: boolean;
}): boolean {
  const scrapLeft = line.qtyScrap > 0.0005 && !line.removedFromArea;
  const salvageLeft = line.qtySalvage > 0.0005 && !line.markedForUse;
  return scrapLeft || salvageLeft;
}

export function assertWasteCautionAcknowledged(
  acknowledgedAt: string | null | undefined,
): { ok: true } | { ok: false; error: string } {
  if (!acknowledgedAt || !acknowledgedAt.trim()) {
    return { ok: false, error: "Acknowledge the waste-walk caution before adding a line." };
  }
  return { ok: true };
}

export type WasteTrajectoryWindow = "today" | "7d" | "month";

export function trajectoryWindowStart(window: WasteTrajectoryWindow, now: Date): string {
  if (window === "7d") return new Date(now.getTime() - 7 * 86400000).toISOString();
  if (window === "month") {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
  }
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString();
}

export type TrajectoryWasteLine = {
  qtySeen: number;
  qtySalvage: number;
  qtyScrap: number;
  unitCost: number;
};

export type TrajectoryWriteOff = {
  deltaQty: number;
  unitCost: number;
};

function roundMoney(value: number): number {
  return Number((value + Number.EPSILON).toFixed(2));
}

/** Walked items only. Write-offs are unreversed negative packed-audit deltas. */
export function trajectoryDollars(
  lines: readonly TrajectoryWasteLine[],
  writeOffs: readonly TrajectoryWriteOff[],
): {
  inWasteAreasUsd: number;
  salvageUsd: number;
  scrapUsd: number;
  packedWriteOffUsd: number;
} {
  let seen = 0;
  let salvage = 0;
  let scrap = 0;
  for (const line of lines) {
    seen += line.qtySeen * line.unitCost;
    salvage += line.qtySalvage * line.unitCost;
    scrap += line.qtyScrap * line.unitCost;
  }
  let packed = 0;
  for (const row of writeOffs) {
    if (row.deltaQty >= -0.0005) continue;
    packed += Math.abs(row.deltaQty) * row.unitCost;
  }
  return {
    inWasteAreasUsd: roundMoney(seen),
    salvageUsd: roundMoney(salvage),
    scrapUsd: roundMoney(scrap),
    packedWriteOffUsd: roundMoney(packed),
  };
}

export function wasteWalkNotes(walkId: string, kind: "scrap" | "salvage"): string {
  if (kind === "scrap") return `Waste walk ${walkId} — scrap`;
  return `Waste walk ${walkId} — salvage return`;
}
